import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errorMessage, API_BASE } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import { toast } from "sonner";
import { RefreshCw, Download, FileText, ArrowLeft, UserX } from "lucide-react";

function isoDate(d) { return d.toISOString().slice(0, 10); }
function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start: isoDate(start), end: isoDate(now) };
}
function money(n) {
  if (n == null || Number.isNaN(n)) return "$0";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/**
 * NoShowReport — dedicated drilldown page reached by clicking the
 * "No-Show Officers" KPI tile. Shows the full list of guards who did not
 * clock in for a past shift within the selected date + site filters.
 *
 * Filters mirror the Operational Metrics tab so a caller can pivot from
 * the summary tile to this list without re-choosing their scope. Callers
 * can pass `?start=YYYY-MM-DD&end=YYYY-MM-DD` in the URL to open the
 * report pre-scoped to the tile's window.
 */
export default function NoShowReport() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [{ start, end }, setRange] = useState(() => {
    const s = searchParams.get("start");
    const e = searchParams.get("end");
    if (s && e) return { start: s, end: e };
    return currentMonthRange();
  });
  const [entity, setEntity] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (entity.site_ids?.length) params.site_id = entity.site_ids.join(",");
      if (entity.client_names?.length) params.client_name = entity.client_names.join(",");
      const res = await api.get("/metrics/operational", { params });
      setData(res.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load no-show report"));
      setData(null);
    }
    setLoading(false);
  }, [start, end, entity]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const exportTo = (fmt) => {
    const params = new URLSearchParams({ start, end, format: fmt });
    if (entity.site_ids?.length) params.set("site_id", entity.site_ids.join(","));
    if (entity.client_names?.length) params.set("client_name", entity.client_names.join(","));
    window.open(`${API_BASE}/metrics/operational?${params.toString()}`, "_blank", "noopener");
  };

  const filtersShow = useMemo(() => ({ client: true, site: true, guard: false }), []);
  const rows = useMemo(() => data?.no_show_guards || [], [data]);
  const count = data?.no_show_count ?? rows.length;

  const totalHours = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.unrealized_hours) || 0), 0),
    [rows],
  );
  const totalRevenue = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.unrealized_revenue) || 0), 0),
    [rows],
  );

  return (
    <div data-testid="no-show-report-page">
      <PageHeader
        title="No-Show Officers"
        subtitle="Every past shift within the selected range where the assigned officer never clocked in."
        action={
          <div className="flex items-center gap-2">
            <GhostButton onClick={() => nav(-1)} data-testid="no-show-back">
              <ArrowLeft className="size-3.5" /> Back
            </GhostButton>
            <GhostButton data-testid="no-show-refresh" onClick={fetchMetrics}>
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </GhostButton>
            <GhostButton data-testid="no-show-export-csv" onClick={() => exportTo("csv")}>
              <Download className="size-3.5" /> CSV
            </GhostButton>
            <GhostButton data-testid="no-show-export-pdf" onClick={() => exportTo("pdf")}>
              <FileText className="size-3.5" /> PDF
            </GhostButton>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {/* Filters */}
        <div className="space-y-3" data-testid="no-show-filters">
          <LinkedEntityFilters
            testIdPrefix="no-show-filter"
            value={entity}
            onChange={setEntity}
            show={filtersShow}
          />
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="label-mute mb-1">Start</div>
              <input
                data-testid="no-show-start"
                type="date"
                className={inputClass}
                value={start}
                onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              />
            </div>
            <div>
              <div className="label-mute mb-1">End</div>
              <input
                data-testid="no-show-end"
                type="date"
                className={inputClass}
                value={end}
                onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            data-testid="no-show-summary-count"
            className="border border-[#27272A] bg-[#121212] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="label-mute">Officers No-Show</div>
              <UserX className={`size-4 ${count > 0 ? "text-[#FF3B30]" : "text-[#34C759]"}`} />
            </div>
            <div className={`font-display text-3xl font-black tracking-tight tabular-nums mt-2 ${count > 0 ? "text-[#FF3B30]" : "text-[#34C759]"}`}>
              {count}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              past shifts with no clock-in
            </div>
          </div>
          <div
            data-testid="no-show-summary-hours"
            className="border border-[#27272A] bg-[#121212] p-4"
          >
            <div className="label-mute">Hours Lost</div>
            <div className="font-display text-3xl font-black tracking-tight tabular-nums mt-2 text-[#FF9F0A]">
              {totalHours.toFixed(1)}h
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              across all no-shows
            </div>
          </div>
          <div
            data-testid="no-show-summary-revenue"
            className="border border-[#27272A] bg-[#121212] p-4"
          >
            <div className="label-mute">Revenue at Risk</div>
            <div className="font-display text-3xl font-black tracking-tight tabular-nums mt-2 text-[#FF3B30]">
              {money(totalRevenue)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              default rate ${data?.default_bill_rate ?? 28}/hr
            </div>
          </div>
        </div>

        {/* Report table */}
        {loading ? (
          <div
            data-testid="no-show-loading"
            className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]"
          >
            Loading no-show report…
          </div>
        ) : (
          <div data-testid="no-show-report-body">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-mute">Report</div>
                <div className="text-xs text-zinc-500">
                  Sorted by shift start, most recent first. Export CSV/PDF above.
                </div>
              </div>
              <PillBadge tone={count > 0 ? "red" : "green"}>
                {count} row{count === 1 ? "" : "s"}
              </PillBadge>
            </div>
            {rows.length === 0 ? (
              <div
                className="text-center py-12 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]"
                data-testid="no-show-empty"
              >
                No no-shows in this range.
              </div>
            ) : (
              <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-[#27272A]">
                    <tr>
                      <th className="px-3 py-2 text-left"><div className="label-mute">Shift Start</div></th>
                      <th className="px-3 py-2 text-left"><div className="label-mute">Officer</div></th>
                      <th className="px-3 py-2 text-left"><div className="label-mute">Badge</div></th>
                      <th className="px-3 py-2 text-left"><div className="label-mute">Site</div></th>
                      <th className="px-3 py-2 text-left"><div className="label-mute">Client</div></th>
                      <th className="px-3 py-2 text-right"><div className="label-mute">Hours</div></th>
                      <th className="px-3 py-2 text-right"><div className="label-mute">Revenue Lost</div></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272A]">
                    {rows.slice(0, 500).map((r) => (
                      <tr
                        key={`${r.shift_id}:${r.guard_id}`}
                        className="hover:bg-[#1c1c1e]"
                        data-testid={`no-show-row-${r.shift_id}-${r.guard_id}`}
                      >
                        <td className="px-3 py-2 text-zinc-300">
                          {r.shift_start ? new Date(r.shift_start).toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-2 text-zinc-300">{r.guard_name || "Unknown"}</td>
                        <td className="px-3 py-2 text-zinc-500 font-mono">{r.badge_no || "—"}</td>
                        <td className="px-3 py-2 text-zinc-300">{r.site_name || "—"}</td>
                        <td className="px-3 py-2 text-zinc-500">{r.client_name || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-300">
                          {r.unrealized_hours}h
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[#FF3B30]">
                          {money(r.unrealized_revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
